import React from 'react';
import { useFormik } from 'formik';

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";
import { Info as InfoIcon } from "components/MaterialSymbol/MaterialIcons";
import { Lock as LockIcon } from "components/MaterialSymbol/MaterialIcons";

// core components
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Button from "components/CustomButtons/Button.js";

// config & services
import { DNS_PROVIDERS, AWS_REGIONS, zoneValidationSchema } from './DnsConfig';
import ManagedDnsZonesService from '../../services/ManagedDnsZonesService';

// assets
import awsLogo from "assets/img/providers/aws-route53.png";
import godaddyLogo from "assets/img/providers/godaddy.png";

const useStyles = makeStyles((theme) => ({
  submitButton: {
    float: "right",
    marginTop: "20px"
  },
  cancelButton: {
    float: "right",
    marginTop: "20px",
    marginRight: "10px"
  },
  sectionHeader: {
    marginTop: "25px",
    marginBottom: "5px",
    display: "flex",
    alignItems: "center"
  },
  sectionTitle: {
    fontSize: "0.85rem",
    fontWeight: "bold",
    color: "#2c3e50",
    textTransform: "uppercase",
    letterSpacing: "0.05em"
  },
  sectionSubtitle: {
    fontSize: "0.8rem",
    color: "#888",
    marginBottom: "15px",
    lineHeight: "1.4"
  },
  optionLabel: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    fontSize: "0.9rem"
  },
  miniLogo: {
    height: "20px",
    width: "20px",
    objectFit: "contain"
  },
  infoIcon: {
    fontSize: "14px",
    color: "#999",
    cursor: "pointer",
    marginLeft: "4px"
  },
  lockIcon: {
    fontSize: "16px",
    color: "#2c3e50",
    marginRight: "8px"
  }
}));

export default function ManagedDnsZoneForm({ initialValues, onSuccess, onCancel }) {
  const classes = useStyles();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: initialValues.id || null,
      baseDomain: initialValues.baseDomain || '',
      dnsProvider: initialValues.dnsProvider || DNS_PROVIDERS.ROUTE53,
      hostedZoneId: initialValues.hostedZoneId || '',
      providerConfig: {
        access_key_env: initialValues.providerConfig?.access_key_env || '',
        secret_key_env: initialValues.providerConfig?.secret_key_env || '',
        region: initialValues.providerConfig?.region || 'us-east-1'
      }
    },
    validationSchema: zoneValidationSchema,
    onSubmit: async (values) => {
      try {
        const payload = {
          base_domain: values.baseDomain,
          dns_provider: values.dnsProvider,
          hosted_zone_id: values.dnsProvider === DNS_PROVIDERS.ROUTE53 ? values.hostedZoneId : null,
          provider_config: values.providerConfig
        };

        if (values.id) {
          await ManagedDnsZonesService.update(values.id, payload);
          onSuccess(`Settings for ${values.baseDomain} updated successfully`);
        } else {
          await ManagedDnsZonesService.create(payload);
          onSuccess(`New zone ${values.baseDomain} initialized successfully`);
        }
      } catch (error) {
        console.error('Operation failed:', error);
        alert(error.response?.data?.message || 'Failed to save DNS configuration');
      }
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <GridContainer>
        <GridItem xs={12}>
            <div className={classes.sectionHeader}>
                <Typography className={classes.sectionTitle}>General Configuration</Typography>
            </div>
            <Divider style={{ marginBottom: '15px' }} />
        </GridItem>

        <GridItem xs={12} md={6}>
          <TextField
            fullWidth
            id="baseDomain"
            name="baseDomain"
            label="Base Domain"
            placeholder="e.g. nv2smartdealer.com"
            value={formik.values.baseDomain}
            onChange={formik.handleChange}
            error={formik.touched.baseDomain && Boolean(formik.errors.baseDomain)}
            helperText={formik.touched.baseDomain && formik.errors.baseDomain}
            margin="normal"
          />
        </GridItem>
        <GridItem xs={12} md={6}>
          <TextField
              fullWidth
              select
              id="dnsProvider"
              name="dnsProvider"
              label="DNS Provider"
              value={formik.values.dnsProvider}
              onChange={formik.handleChange}
              margin="normal"
              SelectProps={{
                renderValue: (value) => {
                  const isAWS = value === DNS_PROVIDERS.ROUTE53;
                  return (
                    <div className={classes.optionLabel}>
                      <img src={isAWS ? awsLogo : godaddyLogo} alt="" className={classes.miniLogo} />
                      <span>{isAWS ? 'AWS Route53' : 'GoDaddy'}</span>
                    </div>
                  );
                }
              }}
          >
              <MenuItem value={DNS_PROVIDERS.ROUTE53}>
                  <div className={classes.optionLabel}>
                      <img src={awsLogo} alt="AWS" className={classes.miniLogo} />
                      AWS Route53
                  </div>
              </MenuItem>
              <MenuItem value={DNS_PROVIDERS.GODADDY}>
                  <div className={classes.optionLabel}>
                      <img src={godaddyLogo} alt="GoDaddy" className={classes.miniLogo} />
                      GoDaddy
                  </div>
              </MenuItem>
          </TextField>
        </GridItem>

        {formik.values.dnsProvider === DNS_PROVIDERS.ROUTE53 && (
          <>
            <GridItem xs={12} md={6}>
              <TextField
                fullWidth
                id="hostedZoneId"
                name="hostedZoneId"
                label="AWS Hosted Zone ID"
                value={formik.values.hostedZoneId}
                onChange={formik.handleChange}
                error={formik.touched.hostedZoneId && Boolean(formik.errors.hostedZoneId)}
                helperText={formik.touched.hostedZoneId && formik.errors.hostedZoneId}
                margin="normal"
              />
            </GridItem>
            <GridItem xs={12} md={6}>
              <TextField
                fullWidth
                select
                id="providerConfig.region"
                name="providerConfig.region"
                label="AWS Region"
                value={formik.values.providerConfig.region}
                onChange={formik.handleChange}
                margin="normal"
              >
                {AWS_REGIONS.map(reg => (
                  <MenuItem key={reg} value={reg}>{reg}</MenuItem>
                ))}
              </TextField>
            </GridItem>
          </>
        )}

        {/* Security / Credentials Section */}
        <GridItem xs={12}>
            <div className={classes.sectionHeader}>
                <LockIcon className={classes.lockIcon} />
                <Typography className={classes.sectionTitle}>Security Environment Mapping</Typography>
            </div>
            <Typography variant="body2" className={classes.sectionSubtitle}>
                Map infrastructure credentials defined in your <b>Hosting Provider</b> (Railway/Vercel) to this rule. 
                Enter the environment variable <b>names</b> only.
            </Typography>
            <Divider style={{ marginBottom: '15px' }} />
        </GridItem>

        <GridItem xs={12} md={6}>
          <TextField
            fullWidth
            id="providerConfig.access_key_env"
            name="providerConfig.access_key_env"
            label={
              <span>
                Access Key ENV Name
                <Tooltip title="Enter the NAME of the variable. Do not enter the actual key.">
                  <InfoIcon className={classes.infoIcon} />
                </Tooltip>
              </span>
            }
            placeholder="e.g. AWS_ACCESS_KEY_ID"
            value={formik.values.providerConfig.access_key_env}
            onChange={formik.handleChange}
            error={formik.touched.providerConfig?.access_key_env && Boolean(formik.errors.providerConfig?.access_key_env)}
            helperText={formik.touched.providerConfig?.access_key_env && formik.errors.providerConfig?.access_key_env}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
        </GridItem>
        <GridItem xs={12} md={6}>
          <TextField
            fullWidth
            id="providerConfig.secret_key_env"
            name="providerConfig.secret_key_env"
            label={
              <span>
                Secret Key ENV Name
                <Tooltip title="Enter the variable name containing your Secret API Key.">
                  <InfoIcon className={classes.infoIcon} />
                </Tooltip>
              </span>
            }
            placeholder="e.g. AWS_SECRET_KEY"
            value={formik.values.providerConfig.secret_key_env}
            onChange={formik.handleChange}
            error={formik.touched.providerConfig?.secret_key_env && Boolean(formik.errors.providerConfig?.secret_key_env)}
            helperText={formik.touched.providerConfig?.secret_key_env && formik.errors.providerConfig?.secret_key_env}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
        </GridItem>
      </GridContainer>

      <footer>
        <Button 
          color="primary" 
          type="submit" 
          className={classes.submitButton}
          disabled={formik.isSubmitting}
        >
          {formik.values.id ? (formik.isSubmitting ? 'Saving...' : 'Update Settings') : (formik.isSubmitting ? 'Setting up...' : 'Setup Zone')}
        </Button>
        <Button 
          color="white" 
          onClick={onCancel} 
          className={classes.cancelButton}
          disabled={formik.isSubmitting}
        >
          Cancel
        </Button>
      </footer>
    </form>
  );
}
